import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Store, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

const COUNTRIES = [
  { code: "US", name: "United States", region: "North America" },
  { code: "CA", name: "Canada", region: "North America" },
  { code: "GB", name: "United Kingdom", region: "Europe" },
  { code: "JM", name: "Jamaica", region: "Caribbean" },
  { code: "TT", name: "Trinidad and Tobago", region: "Caribbean" },
  { code: "BB", name: "Barbados", region: "Caribbean" },
  { code: "BS", name: "Bahamas", region: "Caribbean" },
  { code: "NG", name: "Nigeria", region: "Africa" },
  { code: "GH", name: "Ghana", region: "Africa" },
  { code: "KE", name: "Kenya", region: "Africa" },
  { code: "ZA", name: "South Africa", region: "Africa" },
  { code: "BR", name: "Brazil", region: "South America" },
  { code: "FR", name: "France", region: "Europe" },
  { code: "DE", name: "Germany", region: "Europe" },
  { code: "NL", name: "Netherlands", region: "Europe" },
  { code: "AU", name: "Australia", region: "Oceania" },
  { code: "MX", name: "Mexico", region: "North America" },
  { code: "PR", name: "Puerto Rico", region: "Caribbean" },
  { code: "CR", name: "Costa Rica", region: "Central America" },
  { code: "PA", name: "Panama", region: "Central America" },
];

const CATEGORIES = [
  { id: 'Restaurants', name: 'Restaurants' },
  { id: 'Retail', name: 'Retail' },
  { id: 'Services', name: 'Services' },
  { id: 'Professional', name: 'Professional' },
  { id: 'Beauty & Wellness', name: 'Beauty & Wellness' },
  { id: 'Churches', name: 'Churches' },
  { id: 'Non-Profits', name: 'Non-Profits' },
  { id: 'Foundations', name: 'Foundations' },
  { id: 'Charities', name: 'Charities' },
  { id: 'Organizations', name: 'Organizations' },
  { id: 'Photography', name: 'Photography' },
  { id: 'Videography', name: 'Videography' },
  { id: 'Graphic Design', name: 'Graphic Design' },
  { id: 'Museums', name: 'Museums' },
  { id: 'Film Production', name: 'Film Production' },
  { id: 'Music Recording Company', name: 'Music Recording Company' },
  { id: 'Adventures', name: 'Adventures' },
  { id: 'Cruises', name: 'Cruises' },
  { id: 'Vacation Rentals', name: 'Vacation Rentals' },
  { id: 'Travel Agencies', name: 'Travel Agencies' },
];

const EditBusinessPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    phone: '',
    email: '',
    website: '',
    image_url: '',
  });

  const isUSA = formData.country === 'United States';

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const response = await axios.get(`${API}/businesses/${businessId}`);
        const business = response.data;
        
        // Check ownership
        if (business.owner_id !== user?.user_id) {
          toast.error('You are not authorized to edit this business');
          navigate('/dashboard');
          return;
        }
        
        setFormData({
          name: business.name || '',
          description: business.description || '',
          category: business.category || '',
          address: business.address || '',
          city: business.city || '',
          state: business.state || '',
          zip_code: business.zip_code || '',
          country: business.country || 'United States',
          phone: business.phone || '',
          email: business.email || '',
          website: business.website || '',
          image_url: business.image_url || '',
        });
      } catch (error) {
        toast.error('Business not found');
        navigate('/dashboard');
      } finally {
        setFetching(false);
      }
    };

    fetchBusiness();
  }, [businessId, user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      // Clear state and zip if changing to non-US country
      if (name === 'country' && value !== 'United States') {
        newData.state = '';
        newData.zip_code = '';
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation - state and zip only required for US
    if (!formData.name || !formData.description || !formData.category ||
        !formData.address || !formData.city || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // For US, require state and zip
    if (isUSA && (!formData.state || !formData.zip_code)) {
      toast.error('Please fill in state and ZIP code for US businesses');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/businesses/${businessId}`, formData, {
        headers,
        withCredentials: true
      });
      toast.success('Business updated successfully!');
      navigate(`/business/${businessId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update business');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-stone-white">
        <Navbar />
        <div className="pt-24 flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-deep border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-white" data-testid="edit-business-page">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-deep mb-4 transition-colors"
              data-testid="back-dashboard-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
            <h1 className="font-display text-3xl font-bold text-stone-900" data-testid="page-title">
              Edit Business
            </h1>
            <p className="text-stone-600 mt-1">
              Update your business information
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info */}
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-6 flex items-center gap-2">
                <Store className="w-5 h-5 text-emerald-deep" />
                Basic Information
              </h2>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-stone-700">
                    Business Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="business-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="text-stone-700">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="mt-1"
                    data-testid="business-description-input"
                  />
                </div>

                <div>
                  <Label htmlFor="category" className="text-stone-700">
                    Category <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleSelectChange('category', value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="image_url" className="text-stone-700">
                    Image URL
                  </Label>
                  <Input
                    id="image_url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    placeholder="https://example.com/image.jpg"
                    className="mt-1"
                    data-testid="image-url-input"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-deep" />
                Location
              </h2>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="country" className="text-stone-700">
                    Country <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => handleSelectChange('country', value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="address" className="text-stone-700">
                    Street Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city" className="text-stone-700">
                      City <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="city-input"
                    />
                  </div>
                  {isUSA ? (
                    <div>
                      <Label htmlFor="state" className="text-stone-700">
                        State <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.state}
                        onValueChange={(value) => handleSelectChange('state', value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="state-select">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((state) => (
                            <SelectItem key={state} value={state}>
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="state" className="text-stone-700">
                        State/Province/Region
                      </Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="Region (optional)"
                        className="mt-1"
                        data-testid="state-input"
                      />
                    </div>
                  )}
                </div>

                {isUSA ? (
                  <div className="w-1/2">
                    <Label htmlFor="zip_code" className="text-stone-700">
                      ZIP Code <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="zip-input"
                    />
                  </div>
                ) : (
                  <div className="w-1/2">
                    <Label htmlFor="zip_code" className="text-stone-700">
                      Postal Code
                    </Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      placeholder="Postal code (optional)"
                      className="mt-1"
                      data-testid="zip-input"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-6">
                Contact Information
              </h2>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="phone" className="text-stone-700">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-stone-700">Business Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="website" className="text-stone-700">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="website-input"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex-1 h-12 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full"
                data-testid="update-business-btn"
              >
                {loading ? 'Updating...' : 'Update Business'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EditBusinessPage;
